import { EntityKey } from '@pma-entity/pma-entity-types';
import { MojoWellKnownNames } from '@common/constants/mojo-well-known-names';
import { ContactUsFormService } from '@feature/_app_modules/contact-us/desktop-contact-us/contact-us-form.service';
import { OidcAuthService } from '@auth/services/oidc-auth.service';
import { ContactUsService } from '@feature/layout/common/responsive-contact-us/responsive-contact-us.service';
import { map, BehaviorSubject, catchError, tap, of, takeWhile, filter, forkJoin, combineLatest, take, Observable, switchMap } from 'rxjs';
import { Injectable, OnDestroy } from '@angular/core';
import { StateMachine } from 'stateless';
import { ObservableMachine } from '@feature/_app_modules/shared/state-machines/observable-machine';
import { UntypedFormGroup } from '@angular/forms';
import { Subject } from '@feature/layout/common/responsive-contact-us/responsive-contact-us.types';
import { autocomplete, EntityLookupItemDto } from '@feature/pma-entity/pma-entity-types';
import { PmaEntityLookupHttpService } from '@feature/pma-entity/services/pma-entity-lookup-http.service';
import { PlatformLimitedAccessType, UserSettingsService } from '@feature/user-settings';
import { AlertService } from '@feature/layout/common/alert-panel/alert.service';
import { EntityKeyService } from '@feature/pma-entity/services/entity-key.service';
import * as lodash from 'lodash';

export type ContactUsComponentState =
    'initial' |
    'loading' |
    'showDialog' |
    'error' |
    'final' |
    'action'
    ;

export type ContactUsComponentTrigger =
    'load' |
    'loadComplete' |
    'loadFailed' |
    'userEdit' |
    'callerSelected' |
    'unitSelected' |
    'cancel' |
    'close' |
    'submit' |
    'actionFailed' |
    'actionComplete'
    ;

@Injectable({
    providedIn: 'root'
})
export class ContactUsFacadeService implements OnDestroy {

    private isAlive = true;
    private sm = new StateMachine<ContactUsComponentState, ContactUsComponentTrigger>('initial');
    private machine = new ObservableMachine(this.sm);
    private showDialogSubject = new BehaviorSubject<boolean>(false);
    private subjectsSubject = new BehaviorSubject<Subject[]>(null);
    private form: UntypedFormGroup;
    private formSubject = new BehaviorSubject<UntypedFormGroup>(null);
    private units = new BehaviorSubject<EntityLookupItemDto[]>(null);
    private userEmailSubject = new BehaviorSubject<string>(null);
    private callerQueryStream = new BehaviorSubject<string>(null);
    private unitQueryStream = new BehaviorSubject<string>(null);

    private selectedCaller = new BehaviorSubject<EntityKey>(null);
    private selectedUnit = new BehaviorSubject<EntityKey>(null);

    private isOnBehalfOf = false;
    private isOnBehalfOfModeSubject = new BehaviorSubject<boolean>(false);

    private callerEmailSubject = new BehaviorSubject<string>(null);

    /**
     * Emits the caller email address based on the selected caller. Used for mojopma products when raising a call 'on behalf of'
     */
    callerEmail$ = this.callerEmailSubject.asObservable();

    selectedCaller$ = this.selectedCaller.asObservable();
    selectedUnit$ = this.selectedUnit.asObservable();


    /**
     * Emits the user email address. Used for mojo_for_tenant products when the user themselves are raising a call
     */
    userEmail$ = this.userEmailSubject.asObservable();


    /** emits true if the component is being used to raise calls 'on Behalf of' a caller  */
    isOnBehalfOfMode$ = this.isOnBehalfOfModeSubject.asObservable();


    /** Emits a boolean indicator of whether to show the dialog ore not  */
    showDialog$ = this.showDialogSubject.asObservable();

    /** Emits a collection of call subjects */
    subjects$ = this.subjectsSubject.asObservable();

    /** The angular form */
    form$ = this.formSubject.asObservable();

    /** Emits whether to display autocompletes or not */
    displayAutocompletes$: Observable<boolean> = this.shouldDisplayAutocompletes();

    /** Emits all units available */
    units$ = this.units.asObservable();

    /** Emits if the state is 'error' */
    error$ = this.machine.stateIs$('error');

    /** emits a boolean indicator of whether cancel an eligable action */
    canCancel$ = this.machine.canFire$('cancel');

    /** emits a boolean indicator of whether submit an eligable action */
    canSubmit$ = this.machine.canFire$('submit').pipe(map(canFire => canFire && this.form.valid));

    /** suggestion for tenant autocomplete */
    tenantSuggestions$: Observable<EntityLookupItemDto[]> = combineLatest([this.callerQueryStream, this.selectedUnit$]).pipe(filter(([query, unit]) => query !== null), autocomplete(500, (([query, unit]) => this.getSuggestionsLookup(query, 'Tenant', unit))));
    /** suggestion for unit autocomplete */
    unitSuggestions$: Observable<EntityLookupItemDto[]> = combineLatest([this.unitQueryStream, this.selectedCaller$]).pipe(filter(([query, caller]) => query !== null), autocomplete(500, (([query, caller]) => this.getSuggestionsLookup(query, 'Unit', caller))));
    /** suggestion for caller autocomplete */
    callerSuggestions$: Observable<any[]> = combineLatest([this.callerQueryStream, this.selectedUnit$]).pipe(filter(([query, unit]) => query !== null), autocomplete(500, (([query, unit]) => this.getCallerSuggestions(query, unit))));

    getSuggestionsLookup(query: string, type: string, key: EntityKey) {

        if (key) {
            return this.lookupService.entityForEntity(key, type, query);
        }
        return this.lookupService.lookup(type, query);
    }

    getCallerSuggestions(query: string, key: EntityKey): Observable<any[]> {
        const tenantSuggestions = this.getSuggestionsLookup(query, 'Tenant', key);
        const occupantSuggestions = this.getSuggestionsLookup(query, 'Occupant', key);

        return combineLatest([tenantSuggestions, occupantSuggestions]).pipe(
            map(([tenants, occupants]) => {
                let callers: any[] = [];

                if (tenants.length > 0) {
                    callers.push({ label: 'Tenants', items: tenants })
                }

                if (occupants.length > 0) {
                    callers.push({ label: 'Occupants', items: occupants })
                }

                return callers;
            })
        )
    }

    ngOnDestroy(): void {
        this.isAlive = false;
    }

    load() {
        this.sm.fire('load');
    }

    initialise() {
        this.sm.state = 'initial';
    }

    submit(files: File[]) {
        this.sm.fire('submit', this.getFormValue(), files);
    }

    cancel() {
        this.sm.fire('cancel');
    }

    private shouldDisplayAutocompletes(): Observable<boolean> {
        return combineLatest([this.hasMultipleTenants(), this.isOccupantUser()]).pipe(
            map(([hasMultipleTenants, isOccupantUser]) => {
                return hasMultipleTenants && !isOccupantUser;
            })
        )
    }

    private hasMultipleTenants(): Observable<boolean> {
        return this.contactUsService.getEntityLookupItems('Tenant').pipe(filter((tenants) => tenants != undefined), map((tenants: EntityLookupItemDto[]) => tenants.length > 1));
    }

    private isOccupantUser(): Observable<boolean> {
        return this.userSettingsService.getUserSettings().pipe(map(x => x.LimitedAccess.PlatformLimitedAccessType == PlatformLimitedAccessType.Occupant));
    }

    private getFormValue(): any {
        const formValue = {
            Subject: this.form.value.Subject,
            Message: this.form.value.Message,
            EmailAddress: this.form.value.EmailAddress,
            UnitKey: this.form.value.Unit?.Key ? this.form.value.Unit.Key : this.form.value.Unit,
            Entity: this.form.value.Entity?.Key ? this.form.value.Entity.Key : this.form.value.Entity
        };
        return formValue;
    }

    private clearUp() {
        this.callerEmailSubject.next(null);
        this.selectedCaller.next(null);
        this.selectedUnit.next(null);
    }

    private doSubmit(formGroup: UntypedFormGroup, files: File[]) {

      this.userSettingsService.getCurrentProduct().pipe(switchMap(product => {
        const value = this.getFormValue();
        return this.contactUsService.send(value, files, product).pipe(catchError(err => {
          this.sm.fire('actionFailed');
          return of(undefined);
        }))
      })).subscribe((result) => {
        if (result) {
          this.sm.fire('actionComplete');
        }
      });
    }

    private onClose() {
        this.showDialogSubject.next(false);
        this.clearUp();
    }

    callerAutoCompleteChange(query: string) {
        this.callerQueryStream.next(query);
    }
    unitAutoCompleteChange(query: string) {
        this.unitQueryStream.next(query);
    }

    selectCaller(callerKey: EntityKey) {
        this.sm.fire('callerSelected', callerKey);
    }

    selectUnit(unitKey: EntityKey) {
        this.sm.fire('unitSelected', unitKey);
    }

    setDefaultContextValue(entities: EntityLookupItemDto[]) {
        this.entityKeyService.getEntityKey().pipe(take(1), tap(contextKey => {
            const item = entities.find(x => lodash.isEqual(x.Key, contextKey));
            if (item) {
                this.form.controls['Entity'].setValue(item);
                this.selectCaller(item.Key);
            }
        })).subscribe();
    }

    private onSelectCaller(key) {
        this.selectedCaller.next(key);
    }

    private onSelectUnit(key) {
        this.selectedUnit.next(key);
    }

    private getEntities(): Observable<EntityLookupItemDto[]> {
        return this.isOccupantUser().pipe(take(1), switchMap((x) =>
            x ? this.contactUsService.getEntityLookupItems('Occupant') : this.contactUsService.getEntityLookupItems('Tenant')));
    }

    private onLoading() {

        const subjects$ = this.contactUsService.getSubjects();
        const units$ = this.contactUsService.getEntityLookupItems('Unit');
        const entities$ = this.getEntities();
        const userEmail$ = this.authService.getUserProfile().pipe(map(x => x.email), take(1));
        const isOnBehalfOfMode$ = this.userSettingsService.getCurrentProduct().pipe(take(1), map(p => p == MojoWellKnownNames.Products.MojoDashboard));

        this.selectedCaller$.pipe(filter((tenantKey) => tenantKey != null), switchMap((tenantKey) => this.contactUsService.getEmailByKey(tenantKey))).subscribe((email) => {
            this.form.controls['EmailAddress'].setValue(email);
            this.formSubject.next(this.form);
        });

        forkJoin([subjects$, units$, userEmail$, isOnBehalfOfMode$, entities$]).pipe(
            catchError(err => {
                this.sm.fire('loadFailed');
                return of(undefined);
            }),
            tap(([subjects, units, email, isOnBehalfOf, entities]) => {

                this.isOnBehalfOf = isOnBehalfOf;

                this.isOnBehalfOfModeSubject.next(this.isOnBehalfOf);
                this.subjectsSubject.next(subjects);
                this.units.next(units);
                this.userEmailSubject.next(isOnBehalfOf ? null : email);
                this.form = this.contactUsFormService.getForm(subjects, units, isOnBehalfOf ? null : email, entities);
                this.formSubject.next(this.form);
                this.showDialogSubject.next(true);
                this.sm.fire('loadComplete');
                this.form.valueChanges.pipe(takeWhile(() => this.isAlive)).subscribe(_ => {
                    this.sm.canFire('userEdit').then((canFire) => {
                        if (canFire) {
                            this.sm.fire('userEdit')
                        }
                    })
                });
                this.setDefaultContextValue(entities);
            }),
        ).subscribe();
    }

    private submitFailed() {
        this.alertService.alert({ title: 'Submit failed', message: 'Your message failed to send', timeToDisplay: 3000, type: 'error' });
    }

    private loadFailed() {
        this.alertService.alert({ title: 'Load failed', message: 'Unable to load contact us form', timeToDisplay: 3000, type: 'error' });
    }

    private submitComplete() {
        this.form.reset();
        this.alertService.alert({ title: 'Success', message: 'Your Message was sent successfully', timeToDisplay: 3000, type: 'success' });
    }

    constructor(private readonly contactUsService: ContactUsService,
        private readonly userSettingsService: UserSettingsService,
        private readonly authService: OidcAuthService,
        private readonly contactUsFormService: ContactUsFormService,
        private readonly lookupService: PmaEntityLookupHttpService,
        private readonly alertService: AlertService,
        private readonly entityKeyService: EntityKeyService) {

        this.sm.onTransitioned((t) => console.log('transition', t))

        this.sm.configure('initial')
            .permit('load', 'loading');

        this.sm.configure('loading')
            .onEntryFrom('load', () => this.onLoading())
            .permit('loadComplete', 'showDialog')
            .permit('loadFailed', 'error');

        this.sm.configure('showDialog')
            .permitReentry('userEdit')
            .permit('cancel', 'final')
            .permit('submit', 'action')
            .internalTransition('unitSelected', (args) => this.onSelectUnit(args[0]))
            .internalTransition('callerSelected', (args) => this.onSelectCaller(args[0]));

        this.sm.configure('action')
            .onEntryFrom('submit', (_, form, files) => this.doSubmit(form, files))
            .permit('close', 'final')
            .permit('actionComplete', 'final')
            .permit('actionFailed', 'error');

        this.sm.configure('error')
            .onEntryFrom('loadFailed', () => this.loadFailed())
            .onEntryFrom('actionFailed', () => this.submitFailed())
            .permit('cancel', 'final');

        this.sm.configure('final')
            .onEntryFrom('actionComplete', () => this.submitComplete())
            .onEntry(() => this.onClose())
            .permit('load', 'loading');
    }
}
